import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./styles/RefundPage.css";
import { useParams, useNavigate } from "react-router";
import { PrimaryBtn } from "../../components/BasicComponents";
import CustomDropdown from "../../components/CustomDropdown";
import instance from "../../axios";
import { getUserToken } from "../../utils/userAuthenticate";
import { LuImage } from "react-icons/lu";
import useScrollTop from "../../hooks/useScrollTop";

import { createPortal } from "react-dom";
import Spinner from "../../components/Loaders/Spinner";
import { formatStoreCurrency } from "../../assets/currency";
import { MdOutgoingMail } from "react-icons/md";

import { useTranslation } from "react-i18next";
import "../../localisation/index"
import {sanitizeInput} from "@/hooks/useInputSanitization";
// calculate total from a array of object
const countTotal = (obj, key) => {
  const res = obj
    ?.reduce((acc, curr) => {
      const currValue = parseFloat(curr[key]);
      return acc + currValue;
    }, 0);
  return res;
};
const RefundPage = () => {
  const {t} = useTranslation()
  const { id } = useParams();
  useScrollTop();

  const [data, setData] = useState(null);
  const [inputData, setInputData] = useState([]);
  const [refundAmount, setRefundAmount] = useState();
  const [refundOption, setRefundOption] = useState({
    refund_type: "Store-credit",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [taxPercent, setTaxPercent] = useState(0);
  const [refundData, setRefundData] = useState([]);

  const [refundAvailableAmount, setRefundAvailableAmt] = useState(null);
  const [discountPercent, setDiscountPercent] = useState(0);

  const [qcEmail, setQcEmail] = useState("")
  const [emailValue, setCustomerEmail] = useState("")
  const [isEmailPresent, setIsEmailPresent] = useState(false)
  const [giftcardInvolved, setGiftcardInvolved] = useState(false)
  const [emailErrorMsg, setEmailErrMsg] = useState("")
  const emailInputRef = useRef(null)

  const validateEmail=(email)=> {
    const emailRegex = /^[a-zA-Z0-9._-]+(\.[a-zA-Z0-9._-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+$/;
    return emailRegex.test(email) &&
           !/\.{2,}/.test(email) && // Reject double dots
           !/^\./.test(email) &&    // Reject dot at the start
           !/\.$/.test(email);      // Reject dot at the end
  }

  // fetch item data
  const fetchData = useCallback(async (id) => {
    const url = "/order/details";
    const headers = {
      Authorization: getUserToken(),
    };
    const body = {
      orderId: id.toString(),
      refund: true,
    };

    try {
      const res = await instance.post(url, body, { headers });
      const resJson = await res.data;
      const resData = resJson?.data.orders;

      const totalPrice = parseFloat(resData?.total_price);
      const totalTax = parseFloat(resData?.total_tax);
      const taxPercentage = ((totalTax * 100) / (totalPrice - totalTax)).toFixed(4);

      const subTotalPrice = Number(resData?.total_line_items_price)
      let totalDiscountAmount = 0
      resData.discount_codes.length>0 && resData.discount_codes.forEach((item)=>{
        totalDiscountAmount += Number(item.amount)
      })
      const calDiscountPercentage = ((totalDiscountAmount * 100) / subTotalPrice).toFixed(4)
      setDiscountPercent(calDiscountPercentage)

      const customerEmail = resJson?.data?.orders?.customer?.email || ""
      const qcCustomerEmail = resJson?.data?.refund?.customer_email || ""
      const isGiftCardInvolved = resJson?.data?.QCGCInvolved

      if(customerEmail){
        setIsEmailPresent(true)
        setCustomerEmail(customerEmail)
      }else if(qcCustomerEmail){
        setIsEmailPresent(true)
        setCustomerEmail(qcCustomerEmail)
        setQcEmail(qcCustomerEmail)
      }
      setGiftcardInvolved(isGiftCardInvolved)

      setRefundData(resJson?.data?.refund || []);
      setTaxPercent(taxPercentage);
      setRefundAvailableAmt(resJson?.data?.refundableAmount || 0)

      // to calculate refund quantity
      const tempData = [];
      const refundLines = resData?.refunds;

      if (refundLines?.length !== 0) {
        if (refundLines?.length !== 0) {
          refundLines.forEach((refundHistory) => {
            refundHistory?.refund_line_items.forEach((product) => {
              const prodIndex = tempData.findIndex(
                (item) => item.id === product.line_item_id
              );

              if (prodIndex === -1) {
                tempData.push({
                  id: product.line_item_id,
                  qty: product.quantity,
                });
              } else {
                tempData[prodIndex].qty += product.quantity;
              }
            });
          });
        }
      }

      const calculatedData = resData.line_items.map((prod) => {
        const proditem = tempData.find((item) => item.id === prod.id);

        return proditem
          ? { ...prod, quantity: prod.quantity - proditem.qty,fulfillable_quantity: proditem.fulfillable_quantity  }
          : prod;
      });

      setData(calculatedData);

      // for empty array
      const emptyInputArray = resData.line_items.map((prod) => ({
        id: prod.id,
        qty: 0,
        totalPrice: 0,
        totalTax: 0,
        discountAmount: 0
      }));
      setInputData(emptyInputArray);
    } catch (error) {
      console.log(error);
    }
  }, []);

  // to initiate refund
  const handleInitiate = async () => {
    setEmailErrMsg("")
    if(Number(refundAmount)<1){
      alert(t("refundPage.inputAmountErr"))
      return
    }
    if((refundOption?.refund_type === 'Store-credit') || (refundOption?.refund_type === 'Back-to-Source' && giftcardInvolved)){
      const emailValidation = validateEmail(emailValue)
      const errorMsg = !emailValue ? t("refundPage.emailError") : t("refundPage.invalidEmail")
      if(!emailValue || !emailValidation){
        alert(errorMsg)
        return
      }
    }
    setIsLoading(true);

    const lineData = inputData.map((item) => ({
      id: item.id,
      qty: item.qty !== "" ? item.qty : "0",
    }));

    const url = "/refund/initiate";
    const headers = {
      Authorization: getUserToken(),
    };

    const body = {
      orderId: id,
      line_items: lineData,
      amount: refundAmount,
      refund_type: refundOption?.refund_type || null,
      customer_email: emailValue
    };

    try {
      const res = await instance.post(url, body, { headers });
      const resData = res.data;

      if (resData?.success === true) {
        await fetchData(id)
        setIsLoading(false);
      }

      if (typeof resData.message == "object") {
        let msg = resData.message?.errors.amount[0];
        alert(msg);
      } else {
        alert(resData.message);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  // to handle quantity change
  const handleQuantityChange = (itemId, newQty, totalQty, price, taxlines) => {
    const qtyValue = Number(newQty);
    console.log("Quantity")

    if (!isNaN(qtyValue) && qtyValue >= 0 && qtyValue <= totalQty) {
      const itemIndex = inputData.findIndex((item) => item.id === itemId);

      const discountAmount = ((price*discountPercent)/100).toFixed(4)
      const finalSubTotalAmount = price-discountAmount

      const taxPerItem = (finalSubTotalAmount * (taxPercent / 100)).toFixed(4);

      if (itemIndex !== -1) {
        const updatedInputData = [...inputData];
        updatedInputData[itemIndex].qty = newQty;
        updatedInputData[itemIndex].totalPrice = newQty * price;
        updatedInputData[itemIndex].totalTax = (newQty * taxPerItem).toFixed(4);
        updatedInputData[itemIndex].discountAmount = discountAmount * newQty
        setInputData(updatedInputData);
      } else {
        const newItem = {
          id: itemId,
          qty: newQty,
          totalPrice: newQty * price,
          totalTax: (newQty * taxPerItem).toFixed(4),
          discountAmount: discountAmount * newQty
        };
        setInputData((prev) => [...prev, newItem]);
      }
    }
  };

  useEffect(() => {
    fetchData(id);
  }, [id]);

  const resendRefundEmail=async(orderId, transactionId, status, emailValue)=>{
    setEmailErrMsg("")
    const emailValidation = validateEmail(emailValue)
    if(!emailValue || !emailValidation){
      const errorMsg = !emailValue ? t("refundPage.emailError") : t("refundPage.invalidEmail")
      setEmailErrMsg(errorMsg);
      emailInputRef.current.scrollIntoView({ behavior: 'smooth' });
      emailInputRef.current.focus();
      return
    }

    setIsLoading(true);
    const url= status==='completed' ? "/refund/resendMail" : "/refund/resendMailForPending"
    const body={
      order_id: orderId,
      transID: transactionId,
      customer_email: emailValue
    }
    const headers = {
      Authorization: getUserToken(),
    };

    try {
      const res = await instance.post(url, body, { headers });
      const resData = res.data;

      if (resData?.success === true) {
        setIsLoading(false);
        
      }

      if (typeof resData.message == "object") {
        let msg = resData.message?.errors.amount[0];
        alert(msg);
      } else {
        alert(resData.message);
      }
    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false);
    }
  }

  const refundHistoryTable = useMemo(() => {
    return (
      <div className="refund-history-container">
        <div className="refund-page__title">{t("refundPage.refundHistory")}</div>
        <table>
          <tr>
            <th>{t("refundPage.transactionId")}</th>
            <th>{t("refundPage.date")}</th>

            <th>{t("refundPage.amount")}</th>
            <th>{t("refundPage.refundType")}</th>
            <th>{t("refundPage.status")}</th>
            <th>{t("refundPage.action")}</th>
            <th>{t("refundPage.resendEmail")}</th>
          </tr>
          {refundData?.logs?.map((rfItem, index) => {
            return (
              <tr key={index}>
                <td>
                  <abbr
                    title={rfItem?.id}
                    style={{ textDecoration: "underline" }}
                  >
                    XXX{rfItem?.id.slice(-5)}
                  </abbr>
                </td>
                <td>
                  {rfItem?.created_at
                    ? new Date(rfItem?.created_at).toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })
                    : "N/A"}
                </td>
                <td>{rfItem?.total || "NA"}</td>
                <td>{rfItem?.refund_type}</td>
                <td>{rfItem?.status}</td>
                <td>
                  {rfItem?.status !== "completed" ? t("refundPage.completed") : "-"}
                </td>
                <td className="gc-table__actions">
                  {(rfItem?.status?.toLowerCase() === "pending" || (rfItem?.status === "completed" && rfItem?.gc_emailed_at)) ?  (
                    <button className="resend-refund-email" onClick={()=>{resendRefundEmail(refundData?.order_id, rfItem?.id, rfItem?.status, emailValue)}}>
                      <MdOutgoingMail
                        className="gc-table-icons"
                        style={{ height: "23px", width: "23px" }}
                      />
                    </button>
                  ) : "-"}
                </td>
              </tr>
            );
          })}
        </table>
      </div>
    );
  }, [refundData, emailValue]);

  return (
    <div className="refund-page__component component">
      {isLoading &&
        createPortal(<Spinner />, document.getElementById("portal"))}

      <div className="section-box-container">
        <div className="section-box-title bold">{t("refundPage.refundNo")}: {id}</div>
      </div>

      <div className="section-box-container main-component">
        <div className="refund-page__refund-item-data">
          <div>
            {/* refund items */}
            <div className="refund-page__details">
              {data?.map((product, index) => {
                return (
                  <div className="refund-page__product-detail" key={index}>
                    <div className="refund-page__product-data">
                      <LuImage className="gc-table-icons" />
                      <div>
                        <div className="refund-page__product-title">
                          {product?.title}
                        </div>
                        <div className="refund-page__product-price">
                          {formatStoreCurrency(product?.price)}
                        </div>
                      </div>
                    </div>
                    <div className="refund-page__product-count">
                      <input
                        type="number"
                        name="qty"
                        id={product.id}
                        // value={inputData[index]?.qty ? inputData[index]?.qty : ""}
                        value={
                          inputData.find((item) => item.id === product.id)?.qty
                        }
                        onInput={(e) =>
                          handleQuantityChange(
                            product?.id,
                            sanitizeInput(e.target.value, 'number'),
                            product?.quantity,
                            product?.price,
                            product?.tax_lines
                          )
                        }
                        disabled={product?.quantity ===0 || product?.current_quantity === 0 || product?.quantity <= 0}
                      />
                      / {product?.quantity}
                    </div>
                    <div className="refund-page__product-refund-total">
                      {inputData.find((item) => item.id === product.id)?.qty
                        ? formatStoreCurrency(parseFloat(
                            inputData.find((item) => item.id === product.id)
                              ?.qty * product?.price
                          ))
                        : formatStoreCurrency(0)}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* refund history */}
            {refundData?.logs && refundHistoryTable}
          </div>
          <div className="refund-page__summary">
            <div className="refund-page__refund-process">
              <div className="refund-page__title">{t("refundPage.summary")}</div>

              {/* total price estimation */}

              <table className="refund-page__price-summary-table">
                <tr>
                  <td>{t("refundPage.status")}</td>
                  <td>{formatStoreCurrency(countTotal(inputData, "totalPrice"))}</td>
                </tr>
                <tr>
                  <td>{inputData?.length} {t("refundPage.items")}</td>
                  <td></td>
                </tr>
                <tr>
                  <td>{t("refundPage.discount")}</td>
                  <td>-{formatStoreCurrency(countTotal(inputData, "discountAmount"))}</td>
                </tr>
                <tr>
                  <td>{t("refundPage.tax")}</td>
                  <td>{formatStoreCurrency(countTotal(inputData, "totalTax"))}</td>
                </tr>

                <tr id="total-refund">
                  <td>{t("refundPage.refundTotal")}</td>
                  <td>
                    {" "}
                    {formatStoreCurrency((
                      Number(countTotal(inputData, "totalPrice")) - Number(countTotal(inputData, "discountAmount")) +
                      Number(countTotal(inputData, "totalTax"))
                    ))}
                  </td>
                </tr>
              </table>

              <div className="horinzontal-bar"></div>
            </div>
            {(refundOption?.refund_type === 'Store-credit' && !isEmailPresent) ||
              (refundOption?.refund_type === 'Back-to-Source' && !isEmailPresent && giftcardInvolved) ? (
                <div>                  
                  <div className="refund-page__customer">
                    <h5>{t("refundPage.emailError")}</h5>
                    <input
                      ref={emailInputRef}
                      disabled={qcEmail}
                      value={emailValue}
                      onChange={(e) => setCustomerEmail(sanitizeInput(e.target.value, 'email'))}
                      type="email"
                      placeholder={t("refundPage.emailPlaceholder")}
                    />
                  </div>
                  <p className="refund-err-msg">{emailErrorMsg}</p>
                </div>
              ) : null
            }
            <div className="refund-page__available-amount">
              <p>{t("refundPage.availableRefund")} <b>{refundAvailableAmount}</b></p>
            </div>
            <div className="refund-page__refund-shipping">
              <div className="refund-page__shipping-rate">
                <div className="refund-page__title">{t("refundPage.refundAmount")}</div>
                <input
                  type="text"
                  placeholder="Enter Amount"
                  value={refundAmount}
                  onWheel={(e) => e.target.blur()}
                  onChange={(e) => setRefundAmount(sanitizeInput(e.target.value, 'number'))}
                  className="refund-page__shipping-rate-input"
                />
                <div style={{ margin: "10px 0px" }}>
                  <CustomDropdown
                    options={[
                      {
                        title: t("refundPage.storeCredit"),
                        value: "Store-credit",
                      },
                      {
                        title: t("refundPage.backToSource"),
                        value: "Back-to-Source",
                      },
                    ]}
                    emptyText={t("refundPage.selectRefundType")}
                    keyField="refund_type"
                    value={refundOption?.refund_type || "NA"}
                    setvalue={setRefundOption}
                  />
                </div>
                <PrimaryBtn $primary width="100%" onClick={handleInitiate}>
                  {refundOption?.refund_type
                    ? refundOption?.refund_type === "Store-credit"
                      ? t("refundPage.storeCredit")
                      : t("refundPage.backToSource")
                    : t("refundPage.refund")}
                </PrimaryBtn>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPage;
